import React, { useState, useEffect } from "react";
import { api } from "../api";

const PLACEHOLDERS = ["{{client_name}}", "{{premium}}", "{{carrier}}", "{{effective_date}}", "{{agent_name}}", "{{policy_type}}", "{{deductible}}"];

const EMPTY_CONFIG = {
  config_name: '', ai_agent_name: '', licensed_agent_name: '', licensed_agent_phone: '',
  texting_style: 'Friendly & Casual', lead_type: 'General',
  custom_opening_message: '', custom_followup_day1: '', custom_followup_day3: '', custom_appt_confirmation: ''
};

export default function TemplatesWorkflows() {
  const [tab, setTab] = useState("templates");

  // Templates
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [tForm, setTForm] = useState({ policy_type: "", subject: "", body: "" });
  const [tSaved, setTSaved] = useState(false);
  const [isNew, setIsNew] = useState(false);

  // Workflows
  const [workflows, setWorkflows] = useState([]);
  const [editingWF, setEditingWF] = useState(null);
  const [wForm, setWForm] = useState({ name: "", steps: [{ message: "", wait_minutes: 0 }] });
  const [wSaved, setWSaved] = useState(false);
  const [activeWorkflowId, setActiveWorkflowId] = useState(null);

  // AI Configs
  const [aiConfigs, setAiConfigs] = useState([]);
  const [activeAiConfigId, setActiveAiConfigId] = useState(null);
  const [editingConfig, setEditingConfig] = useState(null);
  const [configForm, setConfigForm] = useState({ ...EMPTY_CONFIG });
  const [configSaved, setConfigSaved] = useState(false);

  const loadTemplates = () => api.getTemplates().then(data => setTemplates(data || [])).catch(() => {});
  useEffect(() => {
    loadTemplates();
    api.getWorkflows().then(setWorkflows).catch(() => {});
    api.me().then(u => {
      setAiConfigs(u.ai_configs || []);
      setActiveAiConfigId(u.active_ai_config_id || null);
      setActiveWorkflowId(u.active_workflow_id || null);
    }).catch(() => {});
  }, []);

  // ── Template handlers ──────────────────────────────────────────────────────
  const handleSelect = (t) => { setSelected(t); setIsNew(false); setTForm({ policy_type: t.policy_type, subject: t.subject || "", body: t.body || "" }); setTSaved(false); };
  const handleNew = () => { setSelected(null); setIsNew(true); setTForm({ policy_type: "", subject: "", body: "" }); setTSaved(false); };
  const handleDeleteTemplate = async (t) => {
    if (!window.confirm("Delete this template?")) return;
    try {
      await api.deleteTemplate(t.id || t.policy_type);
      await loadTemplates();
      if (selected && selected.policy_type === t.policy_type) { setSelected(null); setIsNew(false); }
    } catch (err) { alert(err.message); }
  };
  const handleSaveTemplate = async () => {
    if (!tForm.policy_type.trim()) return alert("Please enter a template name.");
    try {
      await api.saveTemplate({ policy_type: tForm.policy_type, subject: tForm.subject, body: tForm.body });
      await loadTemplates(); setTSaved(true); setTimeout(() => setTSaved(false), 3000);
    } catch (err) { alert(err.message); }
  };

  // ── Workflow handlers ──────────────────────────────────────────────────────
  const handleNewWF = () => { setEditingWF("new"); setWForm({ name: "", steps: [{ message: "", wait_minutes: 0 }] }); };
  const handleEditWF = (w) => { setEditingWF(w._id || w.id); setWForm({ name: w.name, steps: w.steps }); };
  const handleDeleteWF = async (id) => {
    if (!window.confirm("Delete this workflow?")) return;
    try {
      await api.deleteWorkflow(id);
      setWorkflows(ws => ws.filter(w => (w._id || w.id) !== id));
      if (editingWF === id) setEditingWF(null);
      if (activeWorkflowId === id) {
        setActiveWorkflowId(null);
        await api.updateSettings({ active_workflow_id: null }).catch(() => {});
      }
    } catch (err) { alert(err.message); }
  };
  const addStep = () => setWForm(f => ({ ...f, steps: [...f.steps, { message: "", wait_minutes: 0 }] }));
  const removeStep = (i) => setWForm(f => ({ ...f, steps: f.steps.filter((_, idx) => idx !== i) }));
  const updateStep = (i, key, val) => setWForm(f => ({ ...f, steps: f.steps.map((s, idx) => idx === i ? { ...s, [key]: val } : s) }));
  const handleSaveWF = async () => {
    if (!wForm.name || !wForm.steps.some(s => s.message)) return;
    try {
      if (editingWF === "new") {
        const w = await api.createWorkflow(wForm);
        setWorkflows(ws => [...ws, w]);
        setEditingWF(w._id || w.id);
      } else {
        await api.updateWorkflow(editingWF, wForm);
        setWorkflows(ws => ws.map(w => (w._id || w.id) === editingWF ? { ...w, ...wForm } : w));
      }
      setWSaved(true); setTimeout(() => setWSaved(false), 2000);
    } catch (err) { alert(err.message); }
  };
  const handleSetActiveWorkflow = async (id) => {
    const newId = activeWorkflowId === id ? null : id;
    setActiveWorkflowId(newId);
    await api.updateSettings({ active_workflow_id: newId }).catch(err => alert(err.message));
  };

  // ── AI Config handlers ─────────────────────────────────────────────────────
  const handleNewConfig = () => { setEditingConfig('new'); setConfigForm({ ...EMPTY_CONFIG }); setConfigSaved(false); };
  const handleEditConfig = (c) => { setEditingConfig(c.id); setConfigForm({ ...c }); setConfigSaved(false); };
  const handleDeleteConfig = async (id) => {
    if (!window.confirm('Delete this configuration?')) return;
    const newConfigs = aiConfigs.filter(c => c.id !== id);
    const newActiveId = activeAiConfigId === id ? (newConfigs[0]?.id || null) : activeAiConfigId;
    setAiConfigs(newConfigs);
    setActiveAiConfigId(newActiveId);
    if (editingConfig === id) setEditingConfig(null);
    await api.updateSettings({ ai_configs: newConfigs, active_ai_config_id: newActiveId }).catch(err => alert(err.message));
  };
  const handleSaveConfig = async () => {
    if (!configForm.config_name.trim()) return alert('Please enter a configuration name.');
    const id = editingConfig === 'new' ? Date.now().toString() : editingConfig;
    const updated = { ...configForm, id };
    const newConfigs = editingConfig === 'new' ? [...aiConfigs, updated] : aiConfigs.map(c => c.id === id ? updated : c);
    const newActiveId = (newConfigs.length === 1 && !activeAiConfigId) ? id : activeAiConfigId;
    setAiConfigs(newConfigs);
    setActiveAiConfigId(newActiveId);
    setEditingConfig(id);
    await api.updateSettings({ ai_configs: newConfigs, active_ai_config_id: newActiveId }).catch(err => alert(err.message));
    setConfigSaved(true); setTimeout(() => setConfigSaved(false), 2000);
  };
  const handleSetActiveConfig = async (id) => {
    setActiveAiConfigId(id);
    await api.updateSettings({ ai_configs: aiConfigs, active_ai_config_id: id }).catch(err => alert(err.message));
  };

  // ── Styles ─────────────────────────────────────────────────────────────────
  const cardStyle = { background: "var(--bg2)", borderRadius: 16, border: "0.5px solid rgba(83,74,183,0.3)", overflow: "hidden", boxShadow: "0 0 30px rgba(83,74,183,0.08)" };
  const activeCardStyle = { ...cardStyle, border: "0.5px solid #1D9E75", boxShadow: "0 0 30px rgba(29,158,117,0.15)" };
  const headerStyle = { padding: "14px 20px", borderBottom: "0.5px solid var(--border)", background: "linear-gradient(135deg,rgba(83,74,183,0.08),rgba(29,158,117,0.06))", display: "flex", alignItems: "center", gap: 10 };
  const dotStyle = { width: 8, height: 8, borderRadius: "50%", background: "#534AB7", boxShadow: "0 0 8px #534AB7" };
  const activeDotStyle = { ...dotStyle, background: "#1D9E75", boxShadow: "0 0 8px #1D9E75" };
  const labelStyle = { fontSize: 11, fontWeight: 600, color: "var(--text2)", marginBottom: 5, textTransform: "uppercase", letterSpacing: "0.5px" };
  const inputStyle = { width: "100%", padding: "8px 12px", borderRadius: 8, border: "0.5px solid var(--border2)", background: "var(--bg3)", fontSize: 12, color: "var(--text)", boxSizing: "border-box" };
  const btnPrimary = { padding: "10px 28px", borderRadius: 8, border: "none", background: "linear-gradient(135deg,#534AB7,#1D9E75)", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", boxShadow: "0 4px 15px rgba(83,74,183,0.3)" };
  const btnSecondary = (isActive) => ({ padding: "10px 16px", borderRadius: 8, border: isActive ? "0.5px solid #1D9E75" : "0.5px solid var(--border2)", background: isActive ? "rgba(29,158,117,0.12)" : "transparent", fontSize: 13, cursor: "pointer", color: isActive ? "#1D9E75" : "var(--text2)", fontWeight: isActive ? 600 : 400 });
  const listItem = (active) => ({ padding: "10px 14px", borderRadius: 10, cursor: "pointer", border: "0.5px solid " + (active ? "rgba(83,74,183,0.5)" : "var(--border)"), background: active ? "rgba(83,74,183,0.08)" : "var(--bg2)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 });
  const activeBadge = { marginLeft: "auto", fontSize: 11, color: "#1D9E75", fontWeight: 700, background: "rgba(29,158,117,0.1)", padding: "2px 8px", borderRadius: 6 };
  const deleteBtn = { background: "none", border: "none", cursor: "pointer", color: "var(--text3)", fontSize: 16, padding: 2, lineHeight: 1 };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, maxWidth: 960 }}>
      {/* Tab switcher */}
      <div style={{ display: "flex", gap: 4, padding: 4, background: "var(--bg3)", borderRadius: 10, width: "fit-content", border: "0.5px solid var(--border)" }}>
        {[{ id: "templates", label: "Email Templates" }, { id: "workflows", label: "Workflows" }, { id: "ai_configs", label: "AI Configs" }].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{ padding: "7px 22px", borderRadius: 8, border: "none", background: tab === t.id ? "linear-gradient(135deg,#534AB7,#1D9E75)" : "transparent", color: tab === t.id ? "#fff" : "var(--text2)", fontSize: 13, fontWeight: tab === t.id ? 600 : 400, cursor: "pointer" }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Email Templates tab ─────────────────────────────────────────────── */}
      {tab === "templates" && (
        <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
          <div style={{ width: 200, flexShrink: 0, display: "flex", flexDirection: "column", gap: 8 }}>
            <button onClick={handleNew} style={btnPrimary}>+ New template</button>
            {templates.map((t, i) => (
              <div key={t.id || t.policy_type || i} style={listItem(selected && selected.policy_type === t.policy_type && !isNew)}>
                <span onClick={() => handleSelect(t)} style={{ fontSize: 12, fontWeight: 500, color: "var(--text)", flex: 1 }}>{t.policy_type}</span>
                <button onClick={e => { e.stopPropagation(); handleDeleteTemplate(t); }} style={deleteBtn}>x</button>
              </div>
            ))}
            {templates.length === 0 && !isNew && <div style={{ fontSize: 12, color: "var(--text3)", textAlign: "center", padding: "16px 0" }}>No templates yet.</div>}
          </div>
          {(selected || isNew) ? (
            <div style={cardStyle}>
              <div style={headerStyle}><div style={dotStyle} /><span style={{ fontSize: 13, fontWeight: 700, color: "var(--text)" }}>{isNew ? "New template" : "Edit: " + tForm.policy_type}</span></div>
              <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 14 }}>
                <div><div style={labelStyle}>Template name *</div><input value={tForm.policy_type} onChange={e => setTForm(f => ({ ...f, policy_type: e.target.value }))} placeholder="e.g. Individual Health..." style={inputStyle} /></div>
                <div><div style={labelStyle}>Email subject</div><input value={tForm.subject} onChange={e => setTForm(f => ({ ...f, subject: e.target.value }))} placeholder="Subject line..." style={inputStyle} /></div>
                <div><div style={labelStyle}>Email body</div><textarea value={tForm.body} onChange={e => setTForm(f => ({ ...f, body: e.target.value }))} rows={10} placeholder="Write your email here..." style={{ ...inputStyle, resize: "vertical", lineHeight: 1.6, fontSize: 13 }} /></div>
                <div>
                  <div style={labelStyle}>Insert placeholder</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {PLACEHOLDERS.map(ph => <button key={ph} onClick={() => setTForm(f => ({ ...f, body: f.body + ph }))} style={{ padding: "4px 10px", borderRadius: 6, border: "0.5px solid var(--border2)", background: "var(--bg3)", fontSize: 11, color: "var(--text2)", cursor: "pointer", fontFamily: "monospace" }}>{ph}</button>)}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                  <button onClick={handleSaveTemplate} style={btnPrimary}>Save template</button>
                  {tSaved && <span style={{ fontSize: 12, color: "#1D9E75" }}>Saved!</span>}
                </div>
              </div>
            </div>
          ) : (
            <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--text3)", fontSize: 13 }}>Select a template or create a new one.</div>
          )}
        </div>
      )}

      {/* ── Workflows tab ───────────────────────────────────────────────────── */}
      {tab === "workflows" && (
        <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
          <div style={{ width: 200, flexShrink: 0, display: "flex", flexDirection: "column", gap: 8 }}>
            <button onClick={handleNewWF} style={btnPrimary}>+ New workflow</button>
            {workflows.map(w => {
              const wId = w._id || w.id;
              const isActive = activeWorkflowId === wId;
              const isEditing = editingWF === wId;
              return (
                <div key={wId} style={{ ...listItem(isEditing), border: isActive ? "0.5px solid #1D9E75" : isEditing ? "0.5px solid rgba(83,74,183,0.5)" : "0.5px solid var(--border)", background: isActive ? "rgba(29,158,117,0.06)" : isEditing ? "rgba(83,74,183,0.08)" : "var(--bg2)" }}>
                  <div onClick={() => handleEditWF(w)} style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, fontWeight: 500, color: "var(--text)" }}>{w.name}</div>
                    <div style={{ fontSize: 10, color: isActive ? "#1D9E75" : "var(--text3)", marginTop: 2 }}>
                      {w.steps?.length} step{w.steps?.length !== 1 ? "s" : ""}
                      {isActive && <span style={{ fontWeight: 700, marginLeft: 4 }}>● Active</span>}
                    </div>
                  </div>
                  <button onClick={e => { e.stopPropagation(); handleDeleteWF(wId); }} style={deleteBtn}>x</button>
                </div>
              );
            })}
            {workflows.length === 0 && !editingWF && <div style={{ fontSize: 12, color: "var(--text3)", textAlign: "center", padding: "16px 0" }}>No workflows yet.</div>}
          </div>
          {editingWF ? (
            <div style={editingWF !== 'new' && activeWorkflowId === editingWF ? activeCardStyle : cardStyle}>
              <div style={headerStyle}>
                <div style={editingWF !== 'new' && activeWorkflowId === editingWF ? activeDotStyle : dotStyle} />
                <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text)" }}>{editingWF === "new" ? "New workflow" : "Edit: " + wForm.name}</span>
                {editingWF !== 'new' && activeWorkflowId === editingWF && <span style={activeBadge}>● Active</span>}
              </div>
              <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 14 }}>
                <div><div style={labelStyle}>Workflow name</div><input value={wForm.name} onChange={e => setWForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Health intro sequence..." style={inputStyle} /></div>
                <div style={labelStyle}>Steps</div>
                {wForm.steps.map((step, i) => (
                  <div key={i} style={{ padding: "14px 16px", borderRadius: 10, border: "0.5px solid var(--border)", background: "var(--bg3)" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: "var(--text)" }}>Step {i + 1}</span>
                      {wForm.steps.length > 1 && <button onClick={() => removeStep(i)} style={{ padding: "2px 8px", borderRadius: 5, border: "0.5px solid #F09595", background: "transparent", fontSize: 11, cursor: "pointer", color: "#A32D2D" }}>Remove</button>}
                    </div>
                    <textarea value={step.message} onChange={e => updateStep(i, "message", e.target.value)} placeholder="Type the message..." rows={3} style={{ ...inputStyle, resize: "vertical", fontSize: 13 }} />
                  </div>
                ))}
                <button onClick={addStep} style={{ width: "100%", padding: "8px", borderRadius: 8, border: "0.5px dashed var(--border2)", background: "transparent", fontSize: 12, cursor: "pointer", color: "var(--text2)" }}>+ Add step</button>
                <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                  <button onClick={handleSaveWF} style={btnPrimary}>Save workflow</button>
                  {editingWF !== 'new' && (
                    <button onClick={() => handleSetActiveWorkflow(editingWF)} style={btnSecondary(activeWorkflowId === editingWF)}>
                      {activeWorkflowId === editingWF ? "✓ Active" : "Set as Active"}
                    </button>
                  )}
                  <button onClick={() => setEditingWF(null)} style={{ padding: "10px 16px", borderRadius: 8, border: "0.5px solid var(--border2)", background: "transparent", fontSize: 13, cursor: "pointer", color: "var(--text2)" }}>Cancel</button>
                  {wSaved && <span style={{ fontSize: 12, color: "#1D9E75" }}>Saved!</span>}
                </div>
              </div>
            </div>
          ) : (
            <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--text3)", fontSize: 13 }}>Select a workflow or create a new one.</div>
          )}
        </div>
      )}

      {/* ── AI Configs tab ──────────────────────────────────────────────────── */}
      {tab === "ai_configs" && (
        <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
          <div style={{ width: 200, flexShrink: 0, display: "flex", flexDirection: "column", gap: 8 }}>
            <button onClick={handleNewConfig} style={btnPrimary}>+ New config</button>
            {aiConfigs.map(c => {
              const isActive = activeAiConfigId === c.id;
              const isEditing = editingConfig === c.id;
              return (
                <div key={c.id} style={{ ...listItem(isEditing), border: isActive ? "0.5px solid #1D9E75" : isEditing ? "0.5px solid rgba(83,74,183,0.5)" : "0.5px solid var(--border)", background: isActive ? "rgba(29,158,117,0.06)" : isEditing ? "rgba(83,74,183,0.08)" : "var(--bg2)" }}>
                  <div onClick={() => handleEditConfig(c)} style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, fontWeight: 500, color: "var(--text)" }}>{c.config_name}</div>
                    <div style={{ fontSize: 10, color: isActive ? "#1D9E75" : "var(--text3)", marginTop: 2 }}>
                      {c.texting_style || 'Friendly & Casual'}
                      {isActive && <span style={{ fontWeight: 700, marginLeft: 4 }}>● Active</span>}
                    </div>
                  </div>
                  <button onClick={e => { e.stopPropagation(); handleDeleteConfig(c.id); }} style={deleteBtn}>x</button>
                </div>
              );
            })}
            {aiConfigs.length === 0 && !editingConfig && <div style={{ fontSize: 12, color: "var(--text3)", textAlign: "center", padding: "16px 0" }}>No configs yet.</div>}
          </div>

          {editingConfig ? (
            <div style={{ flex: 1, ...(editingConfig !== 'new' && activeAiConfigId === editingConfig ? activeCardStyle : cardStyle) }}>
              <div style={headerStyle}>
                <div style={editingConfig !== 'new' && activeAiConfigId === editingConfig ? activeDotStyle : dotStyle} />
                <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text)" }}>{editingConfig === 'new' ? 'New Config' : configForm.config_name || 'Edit Config'}</span>
                {editingConfig !== 'new' && activeAiConfigId === editingConfig && <span style={activeBadge}>● Active</span>}
              </div>
              <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 14 }}>
                {/* Config name */}
                <div>
                  <div style={labelStyle}>Config Name *</div>
                  <input value={configForm.config_name} onChange={e => setConfigForm(f => ({ ...f, config_name: e.target.value }))} placeholder="e.g. Aggressive Mode, Soft Opener, Self-Employed Focus..." style={inputStyle} />
                </div>

                {/* Agent fields — 2-column grid */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                  <div>
                    <div style={labelStyle}>AI Agent Name</div>
                    <input value={configForm.ai_agent_name} onChange={e => setConfigForm(f => ({ ...f, ai_agent_name: e.target.value }))} placeholder="e.g. Alex" style={inputStyle} />
                    <div style={{ fontSize: 10, color: "var(--text3)", marginTop: 4 }}>What the AI introduces itself as</div>
                  </div>
                  <div>
                    <div style={labelStyle}>Licensed Agent Name</div>
                    <input value={configForm.licensed_agent_name} onChange={e => setConfigForm(f => ({ ...f, licensed_agent_name: e.target.value }))} placeholder="e.g. John Smith" style={inputStyle} />
                    <div style={{ fontSize: 10, color: "var(--text3)", marginTop: 4 }}>Used in appointment confirmation</div>
                  </div>
                  <div>
                    <div style={labelStyle}>Licensed Agent Phone</div>
                    <input value={configForm.licensed_agent_phone} onChange={e => setConfigForm(f => ({ ...f, licensed_agent_phone: e.target.value }))} placeholder="e.g. (555) 123-4567" style={inputStyle} />
                    <div style={{ fontSize: 10, color: "var(--text3)", marginTop: 4 }}>Shown to leads when booking</div>
                  </div>
                  <div>
                    <div style={labelStyle}>Texting Style</div>
                    <select value={configForm.texting_style} onChange={e => setConfigForm(f => ({ ...f, texting_style: e.target.value }))} style={inputStyle}>
                      <option>Friendly &amp; Casual</option>
                      <option>Professional</option>
                      <option>Aggressive</option>
                      <option>Soft &amp; Warm</option>
                    </select>
                  </div>
                  <div>
                    <div style={labelStyle}>Lead Type Default</div>
                    <select value={configForm.lead_type} onChange={e => setConfigForm(f => ({ ...f, lead_type: e.target.value }))} style={inputStyle}>
                      <option>General</option>
                      <option>Self-Employed</option>
                      <option>Small Business Owner</option>
                      <option>Family Coverage</option>
                    </select>
                  </div>
                </div>

                {/* Custom message overrides */}
                <div style={{ borderTop: "0.5px solid var(--border)", paddingTop: 14 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text2)", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 12 }}>
                    Custom Message Overrides <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0, color: "var(--text3)" }}>— leave blank to use master prompt defaults</span>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                    <div>
                      <div style={labelStyle}>Custom Opening Message</div>
                      <textarea value={configForm.custom_opening_message} onChange={e => setConfigForm(f => ({ ...f, custom_opening_message: e.target.value }))} rows={2} placeholder="e.g. Hey this is Alex, can I send you some health insurance options? Text N if not interested." style={{ ...inputStyle, resize: "vertical", fontSize: 12, lineHeight: 1.5 }} />
                      <div style={{ fontSize: 10, color: "var(--text3)", marginTop: 4 }}>Overrides the lead-type default. Leave blank to use master prompt template.</div>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                      <div>
                        <div style={labelStyle}>Follow-up Day 1</div>
                        <textarea value={configForm.custom_followup_day1} onChange={e => setConfigForm(f => ({ ...f, custom_followup_day1: e.target.value }))} rows={2} placeholder="e.g. Hey just checking in — still looking for health coverage?" style={{ ...inputStyle, resize: "vertical", fontSize: 12, lineHeight: 1.5 }} />
                      </div>
                      <div>
                        <div style={labelStyle}>Follow-up Day 3</div>
                        <textarea value={configForm.custom_followup_day3} onChange={e => setConfigForm(f => ({ ...f, custom_followup_day3: e.target.value }))} rows={2} placeholder="e.g. Wanted to follow up one more time — options are still available." style={{ ...inputStyle, resize: "vertical", fontSize: 12, lineHeight: 1.5 }} />
                      </div>
                    </div>
                    <div>
                      <div style={labelStyle}>Appointment Confirmation</div>
                      <textarea value={configForm.custom_appt_confirmation} onChange={e => setConfigForm(f => ({ ...f, custom_appt_confirmation: e.target.value }))} rows={3} placeholder={`e.g. Perfect — you're all set! Agent: {{agent_name}} · Phone: {{agent_phone}}`} style={{ ...inputStyle, resize: "vertical", fontSize: 12, lineHeight: 1.5 }} />
                      <div style={{ fontSize: 10, color: "var(--text3)", marginTop: 4 }}>
                        Supports <code style={{ background: "var(--bg3)", padding: "1px 4px", borderRadius: 3 }}>{"{{agent_name}}"}</code> and <code style={{ background: "var(--bg3)", padding: "1px 4px", borderRadius: 3 }}>{"{{agent_phone}}"}</code> placeholders.
                      </div>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                  <button onClick={handleSaveConfig} style={btnPrimary}>Save config</button>
                  {editingConfig !== 'new' && (
                    <button onClick={() => handleSetActiveConfig(editingConfig)} style={btnSecondary(activeAiConfigId === editingConfig)}>
                      {activeAiConfigId === editingConfig ? "✓ Active" : "Set as Active"}
                    </button>
                  )}
                  {configSaved && <span style={{ fontSize: 12, color: "#1D9E75" }}>Saved!</span>}
                </div>
              </div>
            </div>
          ) : (
            <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--text3)", fontSize: 13 }}>Select a config or create a new one.</div>
          )}
        </div>
      )}
    </div>
  );
}
